import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { CirclePause, History as HistoryIcon, LogOut, Settings2, Sparkles, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@lightning/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { LayoutChromeProvider } from '../contexts/LayoutChromeContext';
import { HOWIE_PRACTICE_COMPLETED_EVENT } from '../lib/howieEvents';
import { HOW_DIMENSION_RING } from '../lib/howDimensions';

export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [profileRingCelebration, setProfileRingCelebration] = useState(0);
  // A page in a focused flow (an active dojo round) can reclaim the footer strip.
  const [, setFooterHidden] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const chrome = useMemo(() => ({ setFooterHidden, setHeaderHidden }), []);
  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  useEffect(() => {
    const celebratePractice = () => setProfileRingCelebration((value) => value + 1);
    window.addEventListener(HOWIE_PRACTICE_COMPLETED_EVENT, celebratePractice);
    return () => window.removeEventListener(HOWIE_PRACTICE_COMPLETED_EVENT, celebratePractice);
  }, []);

  const isActive = (pathname: string) => location.pathname === pathname;
  const isDojo = location.pathname === '/dojo'
    || location.pathname.startsWith('/dojo/');
  const activeNav = isActive('/history')
    ? 'history'
    : isDojo
      ? 'dojo'
      : isActive('/pause')
        ? 'pause'
        : isActive('/chat')
          ? 'chat'
          : null;
  const navItemClass = (item: 'chat' | 'dojo') => {
    const base = 'rounded-xl px-2 py-2 text-sm sm:px-3 font-body font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
    const state = activeNav === item
      ? 'cursor-default bg-white/10 text-white'
      : 'text-white/75 hover:bg-white/[0.06] hover:text-white';
    return `${base} focus-visible:ring-white focus-visible:ring-offset-how-space-deep ${state}`;
  };

  const handleDojoNavigation = () => {
    if (isDojo) {
      navigate('/dojo', {
        replace: location.pathname === '/dojo',
        state: { showPracticeLibrary: true },
      });
      return;
    }
    navigate('/dojo');
  };

  return (
    <div
      className="h-screen overflow-hidden p-1 sm:p-1.5"
      style={{
        background: isDojo
          ? 'var(--how-accent-warm)'
          : 'color-mix(in srgb, var(--how-rose) 18%, white)',
      }}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-[20px] border border-white/80 bg-white shadow-card sm:rounded-[24px]">
        {/* A compact floating object inside one shared application canvas. */}
        {!headerHidden && <header
          className="relative mx-auto mt-6 grid h-14 w-[calc(100%-1.5rem)] max-w-[840px] flex-shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center rounded-[18px] border px-3 shadow-lift backdrop-blur-sm sm:mt-8 sm:flex sm:w-[calc(100%-2rem)] sm:px-5"
          style={{
            background:
              'linear-gradient(105deg, var(--how-space-deep) 0%, color-mix(in srgb, var(--how-space-deep) 55%, var(--how-space-mid)) 58%, var(--how-space-mid) 100%)',
            borderColor: 'rgba(255, 255, 255, 0.20)',
          }}
        >
          <button
            onClick={() => navigate('/chat')}
            aria-label="Go to Chat"
            className="group flex shrink-0 items-center justify-self-start rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-how-space-deep"
          >
            <span className="relative hidden h-6 w-[81px] sm:block">
              <img
                src="/logo_white.svg"
                alt=""
                className="h-6 w-auto"
              />
              <span
                className="howie-mark__color howie-mark__color--wordmark"
                aria-hidden="true"
              >
                <span className="howie-mark__tier howie-mark__tier--top" />
                <span className="howie-mark__tier howie-mark__tier--middle" />
                <span className="howie-mark__tier howie-mark__tier--bottom" />
              </span>
            </span>
            <span className="relative block h-6 w-[21px] sm:hidden">
              <img
                src="/mark_white.svg"
                alt=""
                className="h-6 w-auto"
              />
              <span className="howie-mark__color" aria-hidden="true">
                <span className="howie-mark__tier howie-mark__tier--top" />
                <span className="howie-mark__tier howie-mark__tier--middle" />
                <span className="howie-mark__tier howie-mark__tier--bottom" />
              </span>
            </span>
          </button>

          <nav
            aria-label="Primary"
            className="flex items-center gap-0.5 sm:absolute sm:left-1/2 sm:ml-0 sm:-translate-x-1/2 sm:gap-1.5"
          >
            <button
              onClick={() => {
                if (!isActive('/chat')) navigate('/chat', { state: { newConversation: true } });
              }}
              aria-current={activeNav === 'chat' ? 'page' : undefined}
              className={navItemClass('chat')}
            >
              Chat
            </button>
            <button
              onClick={handleDojoNavigation}
              aria-current={activeNav === 'dojo' ? 'page' : undefined}
              className={navItemClass('dojo')}
            >
              Dojo
            </button>
          </nav>

          <div className="flex shrink-0 items-center justify-self-end gap-2 sm:ml-auto sm:border-l sm:border-white/10 sm:pl-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Open account menu"
                  className="relative flex h-8 w-8 flex-none items-center justify-center rounded-full p-px outline-none ring-1 ring-white/20 transition-all hover:brightness-105 hover:ring-white/40 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
                >
                  <span
                    key={profileRingCelebration}
                    className={`absolute inset-0 rounded-full ${
                      profileRingCelebration > 0 ? 'profile-ring--celebrate' : ''
                    }`}
                    style={{
                      background:
                        activeNav === 'pause' ? 'var(--how-accent-warm)' : HOW_DIMENSION_RING,
                    }}
                    aria-hidden="true"
                  />
                  <span
                    className="relative z-10 flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-how-space-deep"
                  >
                    {avatarUrl && !avatarLoadFailed ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        referrerPolicy="no-referrer"
                        onError={() => setAvatarLoadFailed(true)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <User
                        size={15}
                        aria-hidden="true"
                        className="text-white/80"
                      />
                    )}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={10}
                className="w-48 rounded-xl border-black/5 bg-white p-2 text-how-space-deep shadow-popover"
              >
                <DropdownMenuItem
                  onSelect={() => navigate('/your-how')}
                  aria-current={isActive('/your-how') ? 'page' : undefined}
                  className={`cursor-pointer rounded-lg px-2 py-2 text-how-text-secondary focus:bg-how-rose/45 focus:text-how-space-deep ${
                    isActive('/your-how') ? 'bg-how-rose/35 text-how-space-deep' : ''
                  }`}
                >
                  <Sparkles size={15} aria-hidden="true" className="mr-2" />
                  Your HOW
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate('/pause')}
                  aria-current={activeNav === 'pause' ? 'page' : undefined}
                  className={`cursor-pointer rounded-lg px-2 py-2 text-how-text-secondary focus:bg-how-rose/45 focus:text-how-space-deep ${
                    activeNav === 'pause' ? 'bg-how-rose/35 text-how-space-deep' : ''
                  }`}
                >
                  <CirclePause size={15} aria-hidden="true" className="mr-2" />
                  Pause
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate('/history')}
                  aria-current={activeNav === 'history' ? 'page' : undefined}
                  className={`cursor-pointer rounded-lg px-2 py-2 text-how-text-secondary focus:bg-how-rose/45 focus:text-how-space-deep ${
                    activeNav === 'history' ? 'bg-how-rose/35 text-how-space-deep' : ''
                  }`}
                >
                  <HistoryIcon size={15} aria-hidden="true" className="mr-2" />
                  History
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate('/settings/general')}
                  aria-current={location.pathname.startsWith('/settings') ? 'page' : undefined}
                  className={`cursor-pointer rounded-lg px-2 py-2 text-how-text-secondary focus:bg-how-rose/45 focus:text-how-space-deep ${
                    location.pathname.startsWith('/settings')
                      ? 'bg-how-rose/35 text-how-space-deep'
                      : ''
                  }`}
                >
                  <Settings2 size={15} aria-hidden="true" className="mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-black/5" />
                <DropdownMenuItem
                  onSelect={() => void signOut()}
                  className="cursor-pointer rounded-lg px-2 py-2 text-how-text-secondary focus:bg-how-rose/45 focus:text-how-space-deep"
                >
                  <LogOut size={15} aria-hidden="true" className="mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>}

        <main className="relative flex-1 overflow-hidden bg-white">
          <LayoutChromeProvider value={chrome}>
            <Outlet />
          </LayoutChromeProvider>
        </main>
      </div>
    </div>
  );
}
